/**
 * Slip API Client
 * API functions for slip verification and transaction management
 */

import axiosInstance from '@/lib/api/axios-instance'
import { getBusinessId } from '@/lib/api/axios-instance'
import {
  SlipFilters,
  SlipListResponse,
  VerifySlipRequest,
  ManualSlipRequest,
  SlipVerificationResponse,
  BranchListResponse,
  AccountListResponse,
} from '@/src/types/slip'

const BASE_URL = '/api/v2/business'

/**
 * Fetch list of slips with filters
 */
export async function fetchSlips(filters?: SlipFilters): Promise<SlipListResponse> {
  const businessId = getBusinessId()
  if (!businessId) {
    throw new Error('Business ID not found. Please log in again.')
  }

  const params: any = {}
  
  if (filters) {
    if (filters.search) params.search = filters.search
    if (filters.branch_id) params.branch_id = filters.branch_id
    if (filters.account_id) params.account_id = filters.account_id
    if (filters.bank_code) params.bank_code = filters.bank_code
    if (filters.verification_status) params.status = filters.verification_status
    if (filters.channel) params.channel = filters.channel
    if (filters.is_duplicate !== undefined) params.is_duplicate = filters.is_duplicate
    if (filters.date_from) params.date_from = filters.date_from
    if (filters.date_to) params.date_to = filters.date_to
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.limit = filters.per_page
  }
  
  const response = await axiosInstance.get(`${BASE_URL}/${businessId}/transactions`, { params })
  
  // Transform response to match SlipListResponse structure
  return {
    success: response.data.success,
    data: {
      slips: response.data.data?.transactions || [],
      pagination: {
        current_page: response.data.data?.pagination?.page || 1,
        per_page: response.data.data?.pagination?.limit || 10,
        total_items: response.data.data?.pagination?.total_records || 0,
        total_pages: response.data.data?.pagination?.total_pages || 0,
      }
    }
  }
}

/**
 * Verify slip with automatic bank verification
 */
export async function verifySlip(request: VerifySlipRequest): Promise<SlipVerificationResponse> {
  const businessId = getBusinessId()
  if (!businessId) {
    throw new Error('Business ID not found. Please log in again.')
  }

  const formData = new FormData()
  formData.append('image', request.image)
  formData.append('business_id', businessId)
  if (request.branch_id) {
    formData.append('branch_id', request.branch_id)
  }
  
  // dualAuth middleware supports both API Key and JWT
  const response = await axiosInstance.post<{ success: boolean, data: any }>(`${BASE_URL}/${businessId}/verify`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  
  // Backend returns { success, data: { operation_id, transaction, verification, deduplication,
  //   classification, warnings, business_context, cost, ... } }
  const backendData = response.data.data ?? response.data
  return {
    success: response.data.success,
    operation_id: backendData.operation_id,
    transaction: backendData.transaction,
    verification: backendData.verification,
    deduplication: backendData.deduplication,
    classification: backendData.classification,
    warnings: backendData.warnings ?? [],
    business_context: backendData.business_context,
    cost: backendData.cost,
  }
}

/**
 * Create manual slip entry (no bank verification)
 */
export async function createManualSlip(request: ManualSlipRequest): Promise<SlipVerificationResponse> {
  const businessId = getBusinessId()
  if (!businessId) {
    throw new Error('Business ID not found. Please log in again.')
  }

  const formData = new FormData()
  
  formData.append('business_id', businessId)
  formData.append('account_id', request.account_id)
  formData.append('bank_code', request.bank_code)
  formData.append('sender_name', request.sender_name)
  formData.append('amount', request.amount.toString())
  formData.append('transaction_date', request.transfer_date)
  formData.append('status', request.status || 'success')
  
  if (request.image) {
    formData.append('image', request.image)
  }
  
  const response = await axiosInstance.post<{ success: boolean, data: any }>(`${BASE_URL}/${businessId}/transactions/manual-entry`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  
  // Transform response to match SlipVerificationResponse
  const backendData = response.data.data
  return {
    success: response.data.success,
    operation_id: backendData.operation_id,
    transaction: backendData.transaction,
    cost: backendData.credits_used || 0
  }
}

/**
 * Fetch list of branches for current business
 */
export async function fetchBranches(): Promise<BranchListResponse> {
  const response = await axiosInstance.get<BranchListResponse>(`${BASE_URL}/branches`)
  return response.data
}

/**
 * Fetch list of bank accounts for a branch
 */
export async function fetchAccounts(branchId?: string): Promise<AccountListResponse> {
  const params = branchId ? { branch_id: branchId } : {}
  const response = await axiosInstance.get<AccountListResponse>(`${BASE_URL}/accounts`, { params })
  return response.data
}

/**
 * Delete a slip transaction
 */
export async function deleteSlip(slipId: string): Promise<{ success: boolean }> {
  const response = await axiosInstance.delete(`${BASE_URL}/transactions/${slipId}`)
  return response.data
}
